import {
  AHQueryHandlerLoading,
  AHSeparator,
  AHText,
  Box,
  TouchableBox,
} from "arkh";
import React, { useCallback, useImperativeHandle, useMemo } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { ProductExpireDateListItem } from "./ProductExpireDateListItem";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { ProductExpireBatchesQuery } from "./__generated__/ProductExpireBatchesQuery.graphql";
import { ProductExpireBatchesList_StoreProduct$key } from "./__generated__/ProductExpireBatchesList_StoreProduct.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { DateTime } from "luxon";
import { ActivityIndicator } from "react-native";

type Props = {
  productId: string;
  onPressProductExpireBatch: (id: string) => void;
  onPressAdd: () => void;
};

const ProductExpireBatchesList = (props: {
  product: ProductExpireBatchesList_StoreProduct$key;
  onPressProductExpireBatch: (id: string) => void;
  ref: React.RefObject<{ onEndReached: () => void } | null>;
}) => {
  const intl_tokens = useIntlTokens();
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment ProductExpireBatchesList_StoreProduct on StoreProduct
      @refetchable(queryName: "ProductExpireBatchesListRefetchableQuery") {
        activeExpireBatches {
          id
          ...ProductExpireDateListItem_StoreProductExpireBatch
        }
        inActiveExpireBatches(first: $first, after: $after)
          @connection(
            key: "ProductExpireBatchesList_store_inActiveExpireBatches"
          ) {
          edges {
            node {
              id
              expireAtDate {
                isoString
              }
            }
          }
        }
      }
    `,
    props.product
  );

  const listData = useMemo(() => {
    return [
      ...(data?.activeExpireBatches ?? []).map((batch) => ({
        type: "EXPIRE_BATCH" as const,
        ...batch,
      })),
      ...(
        data?.inActiveExpireBatches?.edges?.flatMap((edge) =>
          edge?.node ? [edge.node] : []
        ) ?? []
      ).map((batch) => ({
        type: "INACTIVE_EXPIRE_BATCH" as const,
        ...batch,
      })),
    ];
  }, [data?.activeExpireBatches, data?.inActiveExpireBatches]);

  const { onPressProductExpireBatch } = props;

  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number]; index: number }) => {
      if (item.type === "INACTIVE_EXPIRE_BATCH") {
        return (
          <TouchableBox
            onPress={() => onPressProductExpireBatch(item.id)}
            space="medium"
            color="surface-primary"
          >
            <AHText type="subtitle" color="text-secondary" style={{ flex: 1 }}>
              {intl_tokens.ExpireDate}{" "}
              {DateTime.fromISO(item.expireAtDate.isoString).toFormat(
                "dd-MM-yyyy"
              )}
            </AHText>
          </TouchableBox>
        );
      }
      return (
        <ProductExpireDateListItem
          productExpireBatch={item}
          onPress={() => {
            onPressProductExpireBatch(item.id);
          }}
        />
      );
    },
    [intl_tokens, onPressProductExpireBatch]
  );

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(20);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  useImperativeHandle(props.ref, () => ({
    onEndReached: () => {
      onEndReached();
    },
  }));

  return (
    <>
      {listData.map((item, index) => {
        return (
          <React.Fragment key={item.id}>
            {index === 0 ? null : <AHSeparator />}
            {renderItem({ item, index })}
          </React.Fragment>
        );
      })}
      {isLoadingNext ? (
        <Box spaceVertical="large" key="loading-more">
          <ActivityIndicator />
        </Box>
      ) : null}
    </>
  );
};

const InnerProductExpireBatches = (
  props: Props & {
    fetchKey: string;
    ref: React.RefObject<{ onEndReached: () => void } | null>;
  }
) => {
  const data = useLazyLoadQuery<ProductExpireBatchesQuery>(
    graphql`
      query ProductExpireBatchesQuery($id: ID!, $first: Int!, $after: String) {
        storeProduct(id: $id) {
          ...ProductExpireBatchesList_StoreProduct
        }
      }
    `,
    {
      id: props.productId,
      first: 10,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const product = data.storeProduct;

  if (!product) {
    return null;
  }

  return (
    <ProductExpireBatchesList
      ref={props.ref}
      product={product}
      onPressProductExpireBatch={props.onPressProductExpireBatch}
    />
  );
};

export const ProductExpireBatches = (
  props: Props & { ref: React.RefObject<{ onEndReached: () => void } | null> }
) => {
  return (
    <LazyQueryNetworkBoundrary
      newFetchKeyOnNavigation
      fallback={
        <Box style={{ flex: 1 }} spaceVertical="medium">
          <AHQueryHandlerLoading />
        </Box>
      }
    >
      {(fetchKey) => (
        <InnerProductExpireBatches fetchKey={fetchKey} {...props} />
      )}
    </LazyQueryNetworkBoundrary>
  );
};
